const SOURCE_VERIFIED_DATE = '2026-08-17';
const { buildAdditionalSeoGuides } = require('./seoGuides');

function buildSeoExpansionContent({ agentProofPoints }) {
  const sharedRelatedLinks = [
    { label: 'Compare AI trading platforms', url: '/compare' },
    { label: 'Choose an automated trading platform', url: '/guides/automated-trading-platforms' },
    { label: 'MCP for Agents', url: '/agents' },
    { label: 'View supported brokers', url: '/brokers' },
  ];

  const comparisonPages = [
    {
      path: '/compare/botspot-vs-composer',
      title: 'BotSpot vs Composer | AI Trading Platform Comparison',
      description: 'Compare BotSpot and Composer for AI-assisted strategy creation, research, backtesting, automated execution, and trading workflows.',
      pageType: 'article',
      lastmod: SOURCE_VERIFIED_DATE,
      content: {
        eyebrow: 'AI trading agent vs no-code strategy platform',
        heading: 'BotSpot vs Composer: AI strategy creation and automated trading',
        summary: 'BotSpot starts with a conversational agent for research, approved one-time trades, and complete Lumibot strategy workflows. Composer centers on Symphonies that users can create with AI, edit visually, backtest, and invest in for automated execution.',
        primaryAction: { label: 'Start with BotSpot', url: '/sales?showLogin=1' },
        secondaryAction: { label: 'See MCP setup', url: '/agents' },
        proofPoints: agentProofPoints,
        comparisonRows: [
          {
            capability: 'Starting point',
            botspot: 'Ask an agent to research a question, prepare an approved trade, or turn an idea into a strategy.',
            alternative: 'Describe a strategy to Composer’s AI-assisted editor or build a Symphony from visual blocks.',
          },
          {
            capability: 'Strategy artifact',
            botspot: 'Create and revise inspectable Lumibot strategy code.',
            alternative: 'Build a Symphony from assets, weights, conditions, filters, and groups in a no-code visual editor.',
          },
          {
            capability: 'Backtesting',
            botspot: 'Run historical simulations as part of the generated strategy workflow.',
            alternative: 'Backtest Symphonies and inspect hypothetical metrics, allocations, and comparisons.',
          },
          {
            capability: 'Trading workflow',
            botspot: 'Use supported connected brokers for approved one-time actions or deployed automated strategies.',
            alternative: 'Invest in a Symphony and let Composer trade toward its target allocations.',
          },
        ],
        sections: [
          {
            heading: 'Same broad promise, different product boundary',
            paragraphs: [
              'Both products support natural-language strategy creation and historical backtesting. BotSpot wraps those capabilities inside a broader trading agent that can research, prepare approved one-time trades, generate Lumibot code, and operate through supported broker connections.',
              'Composer presents strategies as Symphonies. Its documented workflow combines AI creation, a visual block editor, backtests, discovery, and automated execution inside Composer.',
            ],
          },
          {
            heading: 'When Composer may fit better',
            bullets: [
              'You prefer a no-code visual strategy made from documented Symphony building blocks.',
              'You want to browse, inspect, edit, or invest in pre-built and community Symphonies.',
              'You want strategy creation, backtesting, and automated allocation execution inside Composer’s brokerage workflow.',
            ],
          },
          {
            heading: 'Backtests do not equal live results',
            paragraphs: [
              'Composer documents its backtests as hypothetical simulations and explains that historical inputs and execution assumptions differ from live trading. BotSpot backtests are historical simulations too. Neither product can guarantee future results.',
            ],
          },
        ],
        faq: [
          {
            question: 'Can both BotSpot and Composer create strategies from natural language?',
            answer: 'Yes. BotSpot can turn a conversation into inspectable Lumibot strategy code. Composer documents an AI-assisted editor that converts a strategy description into a Symphony that can be inserted and backtested.',
          },
          {
            question: 'Does BotSpot require every request to become an algorithm?',
            answer: 'No. BotSpot can research conversationally and, when direct trading is enabled, prepare a supported one-time order for explicit user approval.',
          },
          {
            question: 'Do Composer or BotSpot backtests predict future returns?',
            answer: 'No. Backtests model historical scenarios using assumptions. Market conditions, data, fees, slippage, order eligibility, and live execution can produce different results.',
          },
        ],
        relatedLinks: sharedRelatedLinks,
        sources: [
          { label: 'BotSpot MCP for Agents', url: '/agents' },
          { label: 'Composer product overview', url: 'https://www.composer.trade/' },
          { label: 'Composer Create with AI documentation', url: 'https://help.composer.trade/article/108-create-with-ai' },
          { label: 'Composer backtest basics', url: 'https://help.composer.trade/article/67-backtest-basics' },
          { label: 'Composer automated trading documentation', url: 'https://help.composer.trade/article/65-how-does-composer-trade' },
          { label: 'Lumibot documentation', url: 'https://lumibot.lumiwealth.com/' },
        ],
        verifiedAt: SOURCE_VERIFIED_DATE,
      },
    },
    {
      path: '/compare/botspot-vs-trendspider',
      title: 'BotSpot vs TrendSpider | AI Trading Platform Comparison',
      description: 'Compare BotSpot and TrendSpider for AI-assisted research, strategy creation, backtesting, alerts, broker execution, and automated trading workflows.',
      pageType: 'article',
      lastmod: SOURCE_VERIFIED_DATE,
      content: {
        eyebrow: 'Agent-led workspace vs chart-first trading platform',
        heading: 'BotSpot vs TrendSpider: AI agent workflow or visual strategy tools',
        summary: 'BotSpot starts with an AI agent that can research, prepare an approved one-time trade, or build and operate a Lumibot strategy. TrendSpider starts with charts, market-analysis tools, no-code strategy testing, alerts, bots, and its Sidekick AI analyst.',
        primaryAction: { label: 'Start with BotSpot', url: '/sales?showLogin=1' },
        secondaryAction: { label: 'See MCP setup', url: '/agents' },
        proofPoints: agentProofPoints,
        comparisonRows: [
          {
            capability: 'Starting point',
            botspot: 'Ask an agent to investigate an idea, prepare an approved action, or turn the idea into a strategy.',
            alternative: 'Begin with charts, screeners, conditions, Strategy Tester, or Sidekick analysis inside TrendSpider.',
          },
          {
            capability: 'Strategy creation',
            botspot: 'Create and revise inspectable Lumibot strategy code through an agent-led workflow.',
            alternative: 'Define rules with natural language or point-and-click menus, with JavaScript available for custom indicators.',
          },
          {
            capability: 'Backtesting',
            botspot: 'Ask the agent to run and revise Lumibot backtests as part of the managed strategy lifecycle.',
            alternative: 'Use the no-code Strategy Tester to inspect trade results, risk statistics, and strategy behavior.',
          },
          {
            capability: 'Actions and automation',
            botspot: 'Review and approve a supported one-time broker order, or connect and operate an automated Lumibot strategy.',
            alternative: 'Deploy strategy logic through cloud alerts and bots, supported broker integrations, or webhooks.',
          },
        ],
        sections: [
          {
            heading: 'Different operating models',
            paragraphs: [
              'TrendSpider concentrates chart analysis, condition building, no-code backtesting, alerts, bots, and Sidekick inside its trading platform. BotSpot concentrates the workflow around an agent that can move from research to an approved one-time action or an inspectable Lumibot strategy.',
              'BotSpot capabilities can also be reached through compatible MCP clients such as ChatGPT, Claude, Cursor, and Codex.',
            ],
          },
          {
            heading: 'When TrendSpider may fit better',
            bullets: [
              'Charts, technical conditions, scanners, and visual market analysis are the center of your workflow.',
              'You prefer point-and-click strategy rules and no-code backtesting over generated strategy code.',
              'You want Sidekick analysis, Strategy Tester, and cloud alerts or bots inside one chart-first platform.',
            ],
          },
          {
            heading: 'Risk and limitations',
            paragraphs: [
              'Neither product removes market risk. Backtests are historical simulations, configuration choices can materially change results, and past results do not guarantee future performance.',
            ],
          },
        ],
        faq: [
          {
            question: 'Does TrendSpider have an AI assistant?',
            answer: 'Yes. TrendSpider describes Sidekick as an AI market analyst that can answer questions, analyze charts, review backtest results, and surface insights.',
          },
          {
            question: 'Can BotSpot and TrendSpider both backtest strategies?',
            answer: 'Yes, through different workflows. BotSpot backtests Lumibot strategies through its agent-led lifecycle. TrendSpider provides a no-code Strategy Tester.',
          },
          {
            question: 'Can BotSpot place a trade without first building a strategy?',
            answer: 'When direct trading is enabled, BotSpot can prepare a supported one-time broker order for user review and approval.',
          },
        ],
        relatedLinks: sharedRelatedLinks,
        sources: [
          { label: 'BotSpot MCP for Agents', url: '/agents' },
          { label: 'TrendSpider product overview', url: 'https://trendspider.com/product/' },
          { label: 'TrendSpider strategy development and backtesting', url: 'https://trendspider.com/product/strategy-development-and-backtesting-tools/' },
          { label: 'Lumibot documentation', url: 'https://lumibot.lumiwealth.com/' },
        ],
        verifiedAt: SOURCE_VERIFIED_DATE,
      },
    },
    {
      path: '/compare/botspot-vs-capitalise-ai',
      title: 'BotSpot vs Capitalise.ai | AI Trading Platform Comparison',
      description: 'Compare BotSpot and Capitalise.ai for plain-language strategy creation, research, backtesting, simulation, and live automation.',
      pageType: 'article',
      lastmod: SOURCE_VERIFIED_DATE,
      content: {
        eyebrow: 'AI trading agent vs code-free rule automation',
        heading: 'BotSpot vs Capitalise.ai',
        summary: 'BotSpot combines conversational research, approved one-time trades, and inspectable Lumibot strategy workflows. Capitalise.ai focuses on creating entry and exit rules in everyday language, then backtesting, simulating, or running those rules live.',
        primaryAction: { label: 'Start with BotSpot', url: '/sales?showLogin=1' },
        secondaryAction: { label: 'See MCP setup', url: '/agents' },
        proofPoints: agentProofPoints,
        comparisonRows: [
          {
            capability: 'Starting point',
            botspot: 'Ask an agent to research, prepare an approved trade, or build a complete strategy.',
            alternative: 'Write entry and exit rules in plain language through Capitalise.ai’s Strategy Creation Wizard.',
          },
          {
            capability: 'Strategy representation',
            botspot: 'Generate and revise inspectable Lumibot strategy code.',
            alternative: 'Review parsed conditions, rules, and actions without writing code.',
          },
          {
            capability: 'Testing modes',
            botspot: 'Backtest generated strategies and review logic and assumptions before deployment.',
            alternative: 'Choose among documented Backtest, Simulate, and Run Live modes.',
          },
          {
            capability: 'Trading workflow',
            botspot: 'Use supported connected brokers for approved one-time actions or automated strategy operation.',
            alternative: 'Run confirmed rule-based strategies through Capitalise.ai’s supported broker and integration workflow.',
          },
        ],
        sections: [
          {
            heading: 'Different meanings of conversational trading',
            paragraphs: [
              'Capitalise.ai uses everyday language to define explicit entry and exit conditions. Its confirmation flow exposes parsed rules, actions, execution mode, limits, and strategy settings before a strategy runs.',
              'BotSpot begins with a broader agent conversation. Users can investigate an idea, request an approved one-time action, or create and operate an inspectable Lumibot strategy.',
            ],
          },
          {
            heading: 'When Capitalise.ai may fit better',
            bullets: [
              'You want code-free rules centered on entry conditions, exit conditions, and execution modes.',
              'You prefer a structured wizard that confirms parsed rules and actions before running.',
              'You want documented backtest, live simulation, and live strategy modes inside one rule-automation workflow.',
            ],
          },
          {
            heading: 'Simulation and live execution can diverge',
            paragraphs: [
              'Capitalise.ai documents differences among backtests, simulations, and live trading, including execution, partial fills, slippage, price sources, and fees. Neither simulated mode guarantees live results.',
            ],
          },
        ],
        faq: [
          {
            question: 'Does Capitalise.ai require coding?',
            answer: 'No. Capitalise.ai documents a code-free workflow where users define entry and exit strategies in everyday language and review the interpreted rules before running them.',
          },
          {
            question: 'What does BotSpot add beyond plain-language strategy rules?',
            answer: 'BotSpot can research conversationally, prepare supported one-time trades for explicit approval, and create, revise, backtest, connect, and operate inspectable Lumibot strategies.',
          },
          {
            question: 'Should backtest, simulation, and live results match?',
            answer: 'Not necessarily. Execution, fills, slippage, data, price methodology, and fees can produce different outcomes.',
          },
        ],
        relatedLinks: sharedRelatedLinks,
        sources: [
          { label: 'BotSpot MCP for Agents', url: '/agents' },
          { label: 'Capitalise.ai product overview', url: 'https://support.capitalise.ai/en/articles/2164262-about-capitalise-ai' },
          { label: 'Capitalise.ai strategy confirmation', url: 'https://support.capitalise.ai/en/articles/5982066-confirming-the-strategy' },
          { label: 'Capitalise.ai mode differences', url: 'https://support.capitalise.ai/en/articles/6148015-differences-between-modes' },
          { label: 'Lumibot documentation', url: 'https://lumibot.lumiwealth.com/' },
        ],
        verifiedAt: SOURCE_VERIFIED_DATE,
      },
    },
    {
      path: '/compare/botspot-vs-tradingview',
      title: 'BotSpot vs TradingView | AI Trading Platform Comparison',
      description: 'Compare BotSpot and TradingView for conversational research, chart analysis, Pine Script strategies, backtesting, alerts, and broker workflows.',
      pageType: 'article',
      lastmod: SOURCE_VERIFIED_DATE,
      content: {
        eyebrow: 'AI trading agent vs chart-first analysis platform',
        heading: 'BotSpot vs TradingView: agent-led trading workflow or charts and Pine Script',
        summary: 'BotSpot starts with an AI agent that can research, prepare an approved one-time trade, or build and operate a Lumibot strategy. TradingView starts with Supercharts, indicators, screeners, Pine Script strategies, alerts, and broker-connected chart trading.',
        primaryAction: { label: 'Start with BotSpot', url: '/sales?showLogin=1' },
        secondaryAction: { label: 'See MCP setup', url: '/agents' },
        proofPoints: agentProofPoints,
        comparisonRows: [
          {
            capability: 'Starting point',
            botspot: 'Ask in plain English, inspect the agent work, and decide whether to research further, trade, or automate.',
            alternative: 'Open Supercharts to analyze symbols with charts, indicators, drawings, screeners, news, and alerts.',
          },
          {
            capability: 'Strategy authoring',
            botspot: 'Ask the agent to create and revise inspectable Lumibot strategy code.',
            alternative: 'Write a Pine Script strategy or use a built-in or published community strategy.',
          },
          {
            capability: 'Backtesting',
            botspot: 'Run and revise Lumibot backtests within the same agent-led strategy workflow.',
            alternative: 'Use Pine strategies to simulate hypothetical orders and inspect Strategy Tester reports.',
          },
          {
            capability: 'Broker actions',
            botspot: 'Request a supported one-time broker order, inspect it, and approve it before submission.',
            alternative: 'Connect a supported broker and trade from Supercharts; Pine strategies cannot directly place exchange orders.',
          },
        ],
        sections: [
          {
            heading: 'Different centers of gravity',
            paragraphs: [
              'TradingView centers the workflow on charts. Pine strategies simulate trades through a broker emulator, produce strategy reports, and can generate realtime alerts. TradingView also supports manual order entry through connected brokers.',
              'BotSpot centers the workflow on an AI agent that can move from research to an approved one-time action or an inspectable Lumibot strategy.',
            ],
          },
          {
            heading: 'When TradingView may fit better',
            bullets: [
              'Advanced interactive charts, indicators, drawings, screeners, and visual market monitoring are your main tools.',
              'You already author or use Pine Script indicators and strategies.',
              'You want chart-based manual trading through a TradingView-supported broker.',
            ],
          },
          {
            heading: 'Execution and backtest limits matter',
            paragraphs: [
              'TradingView distinguishes chart trading from Pine strategy simulation. Pine scripts cannot directly place exchange orders; external tooling can act on webhook alerts. Backtests on either platform remain simulations and cannot guarantee future performance.',
            ],
          },
        ],
        faq: [
          {
            question: 'Is BotSpot a replacement for TradingView charts?',
            answer: 'Not directly. TradingView centers on charts, indicators, screeners, Pine Script, and alerts. BotSpot centers on conversational research, approved trading actions, and the Lumibot strategy lifecycle.',
          },
          {
            question: 'Can a TradingView Pine strategy place live broker orders?',
            answer: 'TradingView’s Pine documentation says strategies and indicators cannot directly place exchange orders. They can emit alerts for external execution tools.',
          },
          {
            question: 'Can BotSpot backtest automated strategies?',
            answer: 'Yes. BotSpot can create, revise, and backtest Lumibot strategies. Users remain responsible for reviewing code, assumptions, simulated results, and deployment risk.',
          },
        ],
        relatedLinks: sharedRelatedLinks,
        sources: [
          { label: 'BotSpot MCP for Agents', url: '/agents' },
          { label: 'TradingView Supercharts guide', url: 'https://www.tradingview.com/support/solutions/43000746464-getting-started-with-supercharts/' },
          { label: 'TradingView Pine strategies', url: 'https://www.tradingview.com/pine-script-docs/concepts/strategies/' },
          { label: 'TradingView strategy FAQ', url: 'https://www.tradingview.com/pine-script-docs/faq/strategies/' },
          { label: 'Lumibot documentation', url: 'https://lumibot.lumiwealth.com/' },
        ],
        verifiedAt: SOURCE_VERIFIED_DATE,
      },
    },
    {
      path: '/compare/botspot-vs-trade-ideas',
      title: 'BotSpot vs Trade Ideas | AI Trading Platform Comparison',
      description: 'Compare BotSpot and Trade Ideas for conversational research, AI trade ideas, stock scanning, backtesting, strategy building, and trading operations.',
      pageType: 'article',
      lastmod: SOURCE_VERIFIED_DATE,
      content: {
        eyebrow: 'AI agent vs stock scanner and AI signals',
        heading: 'BotSpot vs Trade Ideas: build your own trading workflow or follow stock signals',
        summary: 'BotSpot starts with a conversational agent that can research a question, prepare an approved one-time trade, or build and operate a Lumibot strategy. Trade Ideas centers on real-time stock scanning, configurable alerts, Holly AI trade suggestions, and scanner-based trading workflows.',
        primaryAction: { label: 'Start with BotSpot', url: '/sales?showLogin=1' },
        secondaryAction: { label: 'See MCP setup', url: '/agents' },
        proofPoints: agentProofPoints,
        comparisonRows: [
          {
            capability: 'Starting point',
            botspot: 'Ask a market or strategy question in plain English, then inspect the agent work and artifacts.',
            alternative: 'Configure stock scans and alerts or review strategies and trades selected by Holly AI.',
          },
          {
            capability: 'AI role',
            botspot: 'Research an idea, prepare an approved direct trade, or turn the idea into a custom Lumibot strategy.',
            alternative: 'Holly presents real-time stock trade suggestions with entries, stops, and targets.',
          },
          {
            capability: 'Strategy building and testing',
            botspot: 'Generate and revise inspectable strategy code, then run Lumibot backtests.',
            alternative: 'Define strategies with alerts, filters, and formulas, then evaluate them with OddsMaker.',
          },
          {
            capability: 'Trading operations',
            botspot: 'Connect a supported broker, review one-time orders, and operate automated strategies.',
            alternative: 'Use Brokerage Plus for one-click orders, simulation, participating brokers, and configured automation.',
          },
        ],
        sections: [
          {
            heading: 'Signal discovery and agent-led strategy development solve different jobs',
            paragraphs: [
              'Trade Ideas is built around finding and ranking stock events with configurable scanners, charts, Holly AI suggestions, OddsMaker backtests, and Brokerage Plus trading tools.',
              'BotSpot begins with an open-ended agent interaction that can move from research to an approved one-time order or to generated, inspectable strategy code and operations.',
            ],
          },
          {
            heading: 'When Trade Ideas may fit better',
            bullets: [
              'Your main job is monitoring real-time stock scanners, alerts, rankings, and charts.',
              'You want Holly AI to surface prepared stock trade suggestions with entries, stops, and targets.',
              'Your workflow already uses Trade Ideas alerts, filters, custom formulas, and Brokerage Plus.',
            ],
          },
          {
            heading: 'Risk and limitations',
            paragraphs: [
              'Neither platform removes trading risk. Trade Ideas documents OddsMaker differences from live execution and recommends backtesting followed by paper testing before live trading.',
            ],
          },
        ],
        faq: [
          {
            question: 'Is Trade Ideas Holly the same kind of AI agent as BotSpot?',
            answer: 'Trade Ideas describes Holly as a virtual assistant that selects strategies and presents real-time stock trade suggestions. BotSpot spans conversational research, approved direct trading, and custom Lumibot strategy operations.',
          },
          {
            question: 'Can Trade Ideas automate trading strategies?',
            answer: 'Yes. Trade Ideas documents Brokerage Plus automation for strategies based on scans and custom formulas, with simulator testing recommended before live use.',
          },
          {
            question: 'Which platform fits a stock-scanner workflow?',
            answer: 'Trade Ideas directly centers on real-time stock scans, alerts, ranked lists, charts, and Holly suggestions. BotSpot fits a broader agent-led strategy workflow.',
          },
        ],
        relatedLinks: sharedRelatedLinks,
        sources: [
          { label: 'BotSpot MCP for Agents', url: '/agents' },
          { label: 'Trade Ideas feature guide', url: 'https://www.trade-ideas.com/guide/chapter/8_2/8.2New_Tab.html' },
          { label: 'Trade Ideas Holly AI guide', url: 'https://www.trade-ideas.com/hollyguide/' },
          { label: 'Trade Ideas Brokerage Plus guide', url: 'https://www.trade-ideas.com/guide/chapter/21/21Brokerage_Plus.html' },
          { label: 'Trade Ideas OddsMaker guide', url: 'https://forums.trade-ideas.com/guide/chapter/22/22Backtesting_Oddsmaker.html' },
        ],
        verifiedAt: SOURCE_VERIFIED_DATE,
      },
    },
    {
      path: '/compare/botspot-vs-option-alpha',
      title: 'BotSpot vs Option Alpha | AI Trading Bot Comparison',
      description: 'Compare BotSpot and Option Alpha for conversational research, no-code bots, strategy backtesting, automation logic, and trading operations.',
      pageType: 'article',
      lastmod: SOURCE_VERIFIED_DATE,
      content: {
        eyebrow: 'AI-generated strategies vs no-code trading bots',
        heading: 'BotSpot vs Option Alpha: build with an AI agent or configure no-code bots',
        summary: 'BotSpot uses a conversational agent to research, prepare approved one-time trades, and create or operate inspectable Lumibot strategies. Option Alpha provides no-code stock and options bots built from templates, recipes, decisions, actions, scanners, and monitors.',
        primaryAction: { label: 'Start with BotSpot', url: '/sales?showLogin=1' },
        secondaryAction: { label: 'See MCP setup', url: '/agents' },
        proofPoints: agentProofPoints,
        comparisonRows: [
          {
            capability: 'Starting point',
            botspot: 'Describe a research question, trade request, or strategy in plain English.',
            alternative: 'Customize a bot template or create a bot from recipes and predefined automation components.',
          },
          {
            capability: 'Strategy representation',
            botspot: 'Create and revise inspectable Lumibot strategy code through conversation.',
            alternative: 'Build no-code logic from actions, decisions, loops, scanner automations, and monitor automations.',
          },
          {
            capability: 'Backtesting',
            botspot: 'Run backtests on generated Lumibot strategies, then revise code and assumptions.',
            alternative: 'Backtest documented 0DTE and next-day options strategies and generate a bot from a selected test.',
          },
          {
            capability: 'Automation and controls',
            botspot: 'Operate connected strategies and review supported one-time trade requests before submission.',
            alternative: 'Schedule scanners and monitors while bot-level allocation and position controls govern new positions.',
          },
        ],
        sections: [
          {
            heading: 'Generated code and visual recipes provide different kinds of control',
            paragraphs: [
              'Option Alpha packages strategy logic as reusable no-code recipes and decision trees. Its scanners look for entries, monitors manage positions, and bot-level controls constrain allocation and position counts.',
              'BotSpot lets users begin with a broader conversation, inspect generated Lumibot code, request revisions, run backtests, connect a supported broker, and operate the strategy.',
            ],
          },
          {
            heading: 'When Option Alpha may fit better',
            bullets: [
              'You want a no-code bot builder based on templates, recipes, decisions, and visual automation paths.',
              'Your workflow centers on stock or options bots, especially documented 0DTE and next-day options testing.',
              'You prefer predefined scanner and monitor components with bot-level allocation and position controls.',
            ],
          },
          {
            heading: 'Risk and limitations',
            paragraphs: [
              'No automation or backtest guarantees future results. Option Alpha recommends testing automations and paper trading before using live capital. BotSpot users must inspect generated logic and validate assumptions before deployment.',
            ],
          },
        ],
        faq: [
          {
            question: 'Does Option Alpha require coding?',
            answer: 'No. Option Alpha documents automated stock and options strategies built without code from templates, recipes, actions, decisions, scanners, and monitors.',
          },
          {
            question: 'Can BotSpot automate options strategies?',
            answer: 'BotSpot can build and operate Lumibot strategies, but options availability depends on strategy support, connected broker, account permissions, and product eligibility.',
          },
          {
            question: 'Which platform fits users who want to inspect strategy code?',
            answer: 'BotSpot generates inspectable Lumibot strategy code. Option Alpha fits users who prefer no-code recipes and visual automation logic.',
          },
        ],
        relatedLinks: sharedRelatedLinks,
        sources: [
          { label: 'BotSpot MCP for Agents', url: '/agents' },
          { label: 'Option Alpha bots documentation', url: 'https://docs.optionalpha.com/tools/bots' },
          { label: 'Option Alpha automations documentation', url: 'https://docs.optionalpha.com/tools/bots/automations' },
          { label: 'Option Alpha backtesting documentation', url: 'https://docs.optionalpha.com/tools/backtesting' },
          { label: 'Option Alpha testing guidance', url: 'https://docs.optionalpha.com/technical-documentation/troubleshooting/testing-automations' },
        ],
        verifiedAt: SOURCE_VERIFIED_DATE,
      },
    },
    {
      path: '/compare/botspot-vs-public-com',
      title: 'BotSpot vs Public.com | AI Trading Platform Comparison',
      description: 'Compare BotSpot and Public.com for conversational research, AI agents, MCP access, strategy backtesting, brokerage execution, and current connection availability.',
      pageType: 'article',
      lastmod: SOURCE_VERIFIED_DATE,
      content: {
        eyebrow: 'AI strategy workspace vs agentic brokerage',
        heading: 'BotSpot vs Public.com: strategy workspace or agentic brokerage',
        summary: 'Both BotSpot and Public support conversational market workflows, automated trading, and MCP access. BotSpot focuses on building, revising, backtesting, and operating inspectable Lumibot strategies across supported broker connections. Public combines brokerage accounts, native AI Agents, market data, and execution. The current BotSpot broker manifest does not list Public as a supported connection.',
        primaryAction: { label: 'Start with BotSpot', url: '/sales?showLogin=1' },
        secondaryAction: { label: 'View supported BotSpot brokers', url: '/brokers' },
        proofPoints: agentProofPoints,
        comparisonRows: [
          {
            capability: 'Product role',
            botspot: 'AI trading workspace for research, approved one-time trades, and Lumibot strategy workflows using supported connected brokers.',
            alternative: 'Brokerage and multi-asset investing platform with AI Agents, research tools, APIs, and native execution.',
          },
          {
            capability: 'Building automation',
            botspot: 'Create, inspect, revise, and operate strategy code through a conversational agent.',
            alternative: 'Describe conditions and actions in plain English, review the proposed Agent workflow, then activate it inside a Public account.',
          },
          {
            capability: 'Backtesting',
            botspot: 'Backtest inspectable Lumibot strategy code before deciding whether to deploy it.',
            alternative: 'Public’s Agent help documentation lists Agent backtesting as not yet available; Generated Assets has a separate historical custom-index backtest.',
          },
          {
            capability: 'AI and MCP access',
            botspot: 'Use BotSpot or compatible MCP clients including ChatGPT, Claude, Cursor, and Codex.',
            alternative: 'Use built-in Public Agents or connect a Public brokerage account to documented third-party AI clients through Public’s MCP server.',
          },
          {
            capability: 'Authorization model',
            botspot: 'Review and approve a supported one-time trade before submission; review strategy logic before deployment.',
            alternative: 'Review and activate an Agent workflow first; active Agents can then execute approved instructions without confirmation for every transaction.',
          },
        ],
        sections: [
          {
            heading: 'Where BotSpot and Public overlap and diverge',
            paragraphs: [
              'Both products support plain-English research and trading workflows, automation, and MCP access. BotSpot supplies a strategy-building and operations layer around supported connected brokers. Public supplies brokerage accounts plus native Agents, market data, account actions, APIs, and execution.',
              'BotSpot’s clearest distinction is inspectable Lumibot code, revisions, strategy backtests, and a supported multi-broker workflow.',
            ],
          },
          {
            heading: 'When Public may fit better',
            bullets: [
              'You want brokerage, market data, cash movement, and agent execution inside one Public account.',
              'You want native conditional workflows across supported stocks, options, and crypto.',
              'You want Public-specific investing products or direct MCP access to a Public brokerage account.',
            ],
          },
          {
            heading: 'Connection status, availability, and risk',
            paragraphs: [
              'The current BotSpot public broker manifest does not list Public as a supported connection. This page compares separate products and does not imply an integration.',
              'Public’s help center currently describes built-in Agent workflows as web-only and rolling out through a waitlist. Automated trading can lose money, and historical results do not guarantee future performance.',
            ],
          },
        ],
        faq: [
          {
            question: 'Can BotSpot connect directly to Public.com?',
            answer: 'Not through the current standard broker list. Public is not listed in BotSpot’s public broker connection manifest as of August 14, 2026.',
          },
          {
            question: 'Do BotSpot and Public.com both support MCP?',
            answer: 'Yes, but they provide separate MCP products. BotSpot exposes its workflow to compatible AI clients. Public’s MCP server connects a Public brokerage account to documented third-party AI clients.',
          },
          {
            question: 'Can Public Agents backtest a trading strategy?',
            answer: 'Public’s current Agent help documentation lists backtesting as not yet available for Agents. BotSpot can generate and backtest inspectable Lumibot strategy code.',
          },
        ],
        relatedLinks: sharedRelatedLinks,
        sources: [
          { label: 'BotSpot broker connection manifest', url: '/.well-known/botspot-manifest.json' },
          { label: 'Public AI Agents', url: 'https://public.com/ai-agents' },
          { label: 'Public Agents basics', url: 'https://help.public.com/en/articles/15435640-agents-the-basics' },
          { label: 'Public Agent controls', url: 'https://help.public.com/en/articles/15435700-agents-setting-up-and-controlling-agents' },
          { label: 'Public MCP server', url: 'https://public.com/mcp-trading' },
          { label: 'Public Agentic Brokerage disclosures', url: 'https://public.com/disclosures/agenticterms' },
        ],
        verifiedAt: SOURCE_VERIFIED_DATE,
      },
    },
  ];

  const additionalGuidePages = buildAdditionalSeoGuides({ agentProofPoints });

  const withWhoShouldUse = (page) => {
    const sections = page.content?.sections || [];
    if (sections.some((section) => section.heading === 'Who should use which')) return page;
    return {
      ...page,
      lastmod: SOURCE_VERIFIED_DATE,
      content: {
        ...page.content,
        sections: [
          ...sections,
          {
            heading: 'Who should use which',
            paragraphs: [
              'Use BotSpot when you want a conversational agent that can research, prepare an approved one-time trade, and build inspectable Lumibot strategies through currently supported brokers.',
              'Use the other product on this page when its documented workflow is the job you need first: specialist charting, brokerage, no-code allocation, or research tooling rather than the full BotSpot workspace.',
              'Backtests mentioned for either product are historical simulations. They are not live results and do not guarantee future performance. Include drawdown and date range whenever a number is shown.',
            ],
          },
        ],
        verifiedAt: SOURCE_VERIFIED_DATE,
      },
    };
  };

  return {
    comparisonPages: comparisonPages.map(withWhoShouldUse),
    guidePages: [
      {
        path: '/guides/how-to-build-an-ai-trading-bot',
        title: 'How to Build an AI Trading Bot | BotSpot',
        description: 'Turn a trading idea into explicit rules, inspect AI-generated strategy code, backtest it, test it with paper trading, and prepare it for controlled deployment.',
        pageType: 'article',
        lastmod: SOURCE_VERIFIED_DATE,
        content: {
          eyebrow: 'Practical AI trading bot guide',
          heading: 'Build an AI trading bot from testable rules, not a vague prompt',
          summary: 'Define the decision your bot must make, use AI to turn that specification into an inspectable Lumibot strategy, test it against historical data, and validate its operations before putting capital at risk.',
          primaryAction: { label: 'Start building in BotSpot', url: '/ai-agent' },
          secondaryAction: { label: 'Read the backtesting guide', url: '/guides/backtesting-ai-trading-strategies' },
          proofPoints: agentProofPoints,
          sections: [
            {
              heading: '1. Write the trading decision before asking AI for code',
              paragraphs: [
                'Begin with rules another person could follow without guessing. Name the asset universe, required data, evaluation schedule, entry condition, exit condition, position-sizing rule, and conditions that must block trading.',
                'A useful prompt describes observable inputs and actions. A request to build a bot that trades well does not define a testable strategy.',
              ],
              bullets: [
                'Define which symbols or asset-selection rule the strategy may use.',
                'State when the strategy evaluates conditions and which timezone applies.',
                'Separate entry, exit, sizing, and risk rules.',
                'Describe what should happen when data is missing, stale, or invalid.',
              ],
            },
            {
              heading: '2. Generate and inspect the strategy',
              paragraphs: [
                'Ask the BotSpot agent to turn the specification into a Lumibot strategy. Strategy state, lifecycle methods, parameters, data requests, and order behavior should remain visible for review.',
                'AI-generated code is a draft, not evidence that the strategy is correct. Map every generated rule back to the original specification before testing.',
              ],
              bullets: [
                'Check symbol handling, market calendars, timezones, and evaluation timing.',
                'Inspect position sizing, order types, and duplicate-order protection.',
                'Look for hidden defaults and assumptions that were not in the prompt.',
                'Keep broker credentials in protected configuration, never prompts or public code.',
              ],
            },
            {
              heading: '3. Run a backtest with declared assumptions',
              paragraphs: [
                'Choose a historical period and data source that fit the strategy. Record assumptions about execution, fees, slippage, liquidity, and missing data instead of treating defaults as facts.',
                'Verify that each input was available at the simulated decision time. Future information can make an impossible strategy appear convincing.',
              ],
              bullets: [
                'Review trades and timestamps, not only summary performance.',
                'Confirm warm-up periods and indicators use past data only.',
                'Separate development periods from later out-of-sample checks.',
                'Investigate errors, zero-trade runs, and unexpectedly dense trading.',
              ],
            },
            {
              heading: '4. Revise one hypothesis at a time',
              paragraphs: [
                'When a backtest exposes a problem, change one defined rule and record why. Rewriting several rules after every weak result makes learning difficult and increases overfitting risk.',
                'Use revisions to correct implementation errors, clarify rules, or test a stated alternative while preserving earlier results.',
              ],
            },
            {
              heading: '5. Validate operations before live deployment',
              paragraphs: [
                'A backtest does not test credentials, broker permissions, network interruptions, rejected orders, delayed data, or monitoring. Use a supported paper account where available and observe the normal schedule.',
                'Before live use, verify broker support, account permissions, asset eligibility, order behavior, and the stop process. Paper and live modes vary by broker.',
              ],
            },
            {
              heading: 'Limitations and risk',
              paragraphs: [
                'AI can shorten the path from written rules to executable code, but it cannot prove the rules have an edge. Generated code can contain mistakes or unsafe assumptions.',
                'Backtests, simulations, and paper trading are hypothetical. Live results can differ because of latency, liquidity, slippage, fees, partial fills, market impact, outages, and changing market conditions.',
              ],
            },
          ],
          faq: [
            {
              question: 'Do I need to know how to code to build an AI trading bot?',
              answer: 'You can start from plain-English rules and use BotSpot to generate and revise Lumibot code. You still need to inspect logic, assumptions, evidence, and deployment behavior.',
            },
            {
              question: 'Can a BotSpot strategy trade live?',
              answer: 'BotSpot supports automated strategy workflows through listed broker connections, subject to broker, mode, account permissions, asset eligibility, and current BotSpot support.',
            },
            {
              question: 'Will an AI-generated trading bot be profitable?',
              answer: 'No platform or backtest can guarantee profitability. Historical and simulated results are not predictive of future results.',
            },
          ],
          relatedLinks: [
            { label: 'Backtest AI trading strategies', url: '/guides/backtesting-ai-trading-strategies' },
            { label: 'Compare paper and live trading', url: '/guides/paper-trading-vs-live-trading' },
            { label: 'Explore supported brokers', url: '/brokers' },
            { label: 'Use BotSpot through an MCP client', url: '/agents' },
          ],
          sources: [
            { label: 'BotSpot MCP for Agents', url: '/agents' },
            { label: 'BotSpot broker connection manifest', url: '/.well-known/botspot-manifest.json' },
            { label: 'BotSpot Terms of Service', url: '/terms' },
            { label: 'Lumibot lifecycle methods', url: 'https://lumibot.lumiwealth.com/lifecycle_methods.html' },
            { label: 'Lumibot backtesting documentation', url: 'https://lumibot.lumiwealth.com/backtesting.html' },
          ],
          verifiedAt: SOURCE_VERIFIED_DATE,
        },
      },
      {
        path: '/guides/algorithmic-trading-without-coding',
        title: 'Algorithmic Trading Without Coding | BotSpot Guide',
        description: 'Learn how to describe trading rules in plain English, generate inspectable strategy code with AI, backtest it, and validate it without writing every line yourself.',
        pageType: 'article',
        lastmod: SOURCE_VERIFIED_DATE,
        content: {
          eyebrow: 'Plain-English algorithmic trading guide',
          heading: 'Build an algorithmic trading workflow without writing every line yourself',
          summary: 'Use conversation to define strategy rules, generate inspectable Lumibot code, test historical behavior, and prepare a supported broker workflow. No-code input reduces typing; it does not remove review, testing, or risk ownership.',
          primaryAction: { label: 'Describe a strategy to BotSpot', url: '/ai-agent' },
          secondaryAction: { label: 'See how AI bot building works', url: '/guides/how-to-build-an-ai-trading-bot' },
          proofPoints: agentProofPoints,
          sections: [
            {
              heading: '1. Convert the idea into unambiguous rules',
              paragraphs: [
                'Plain English works best when it describes a system rather than a desired outcome. State what the strategy observes, when it evaluates, what causes an order, how large that order may be, and what closes or blocks a position.',
                'Avoid subjective phrases such as strong trend, safe entry, or good stock unless the prompt defines how each phrase is measured.',
              ],
              bullets: [
                'Name the asset universe or selection process.',
                'Define indicators and exact parameters.',
                'Define entry and exit conditions separately.',
                'Set position, exposure, and trading-frequency constraints.',
              ],
            },
            {
              heading: '2. Let the agent produce technical artifacts',
              paragraphs: [
                'BotSpot can turn the specification into a Lumibot strategy, revise the implementation, and run backtests. Users can work inside BotSpot or through a compatible MCP client.',
                'Ask for code plus a plain-English rule map that connects each requirement to its implementation.',
              ],
            },
            {
              heading: '3. Review code even when you did not write it',
              paragraphs: [
                'Algorithmic trading without manual coding is not algorithmic trading without code. Generated code still controls scheduling, data use, order submission, and strategy state.',
                'Review the artifact or use a qualified reviewer. Ask for explanations of any lifecycle method or rule you cannot verify.',
              ],
              bullets: [
                'Verify order sizing and duplicate-order safeguards.',
                'Inspect time comparisons, market calendars, and symbol formats.',
                'Confirm errors cannot silently turn into unintended orders.',
                'Never paste broker passwords, private keys, or API secrets into a prompt.',
              ],
            },
            {
              heading: '4. Test the rules, not the story',
              paragraphs: [
                'Run historical tests using declared dates, data, and execution assumptions. Review individual trades rather than accepting a generated narrative about results.',
                'Do not repeatedly optimize until a preferred number appears. Preserve an out-of-sample period and record why each revision exists.',
              ],
            },
            {
              heading: '5. Connect a broker only after understanding the workflow',
              paragraphs: [
                'BotSpot uses supported third-party brokers for eligible account and execution workflows. Current assets, paper or live modes, authentication methods, and warnings are published in the broker manifest.',
                'Confirm broker permissions, market data, fees, regional eligibility, and order support before paper or live operation.',
              ],
            },
            {
              heading: 'What no-code does not remove',
              paragraphs: [
                'Plain-English creation reduces syntax work. It does not remove ambiguity, data problems, software defects, broker constraints, market risk, or responsibility for deployed code.',
              ],
            },
          ],
          faq: [
            {
              question: 'Is BotSpot truly no-code?',
              answer: 'BotSpot lets users start with plain-English instructions and generates inspectable strategy code. Review remains required because deployment decisions need more than a conversational description.',
            },
            {
              question: 'Which markets can a BotSpot strategy use?',
              answer: 'Available workflows depend on strategy, data, broker, account permissions, and current integration support. Broker pages list current connections and modes.',
            },
            {
              question: 'Can the agent place trades directly?',
              answer: 'When direct trading is enabled, BotSpot can prepare supported one-time trading actions for explicit user approval. Automated deployment is a separate workflow.',
            },
          ],
          relatedLinks: [
            { label: 'Build an AI trading bot', url: '/guides/how-to-build-an-ai-trading-bot' },
            { label: 'Backtest AI trading strategies', url: '/guides/backtesting-ai-trading-strategies' },
            { label: 'Compare paper and live trading', url: '/guides/paper-trading-vs-live-trading' },
            { label: 'Explore supported brokers', url: '/brokers' },
          ],
          sources: [
            { label: 'BotSpot MCP for Agents', url: '/agents' },
            { label: 'BotSpot broker connection manifest', url: '/.well-known/botspot-manifest.json' },
            { label: 'BotSpot Terms of Service', url: '/terms' },
            { label: 'Lumibot lifecycle methods', url: 'https://lumibot.lumiwealth.com/lifecycle_methods.html' },
            { label: 'Lumibot backtesting documentation', url: 'https://lumibot.lumiwealth.com/backtesting.html' },
          ],
          verifiedAt: SOURCE_VERIFIED_DATE,
        },
      },
      {
        path: '/guides/backtesting-ai-trading-strategies',
        title: 'How to Backtest AI Trading Strategies | BotSpot',
        description: 'Build a defensible AI strategy backtest by freezing the hypothesis, checking data timing, documenting execution assumptions, and testing robustness.',
        pageType: 'article',
        lastmod: SOURCE_VERIFIED_DATE,
        content: {
          eyebrow: 'AI strategy backtesting guide',
          heading: 'Backtest an AI trading strategy without mistaking simulation for proof',
          summary: 'Freeze the hypothesis before testing, verify that historical inputs were available at each decision, inspect trades and assumptions, and reserve unseen data for robustness checks.',
          primaryAction: { label: 'Backtest a strategy in BotSpot', url: '/ai-agent' },
          secondaryAction: { label: 'Review BotSpot case studies', url: '/case-studies' },
          proofPoints: agentProofPoints,
          sections: [
            {
              heading: '1. Freeze the hypothesis before running the test',
              paragraphs: [
                'Write what the strategy is expected to exploit and how the rules express that idea. Record universe, data inputs, entry and exit rules, sizing, schedule, and risk constraints before looking at results.',
                'This starting record prevents the strategy story from changing after every result.',
              ],
            },
            {
              heading: '2. Check data timing and historical coverage',
              paragraphs: [
                'A valid simulation uses only information available at each historical decision point. Verify publication timing, bar completion, timezone handling, corporate actions, symbol history, and changing research universes.',
                'Missing or revised history should be documented, not silently replaced.',
              ],
              bullets: [
                'Compare requested dates with actual data coverage.',
                'Check that indicators use completed historical observations.',
                'Look for delisted assets or changing universe membership.',
                'Investigate gaps instead of assuming missing data means no signal.',
              ],
            },
            {
              heading: '3. Document the execution model',
              paragraphs: [
                'Every backtest assumes prices, fills, latency, fees, slippage, liquidity, partial fills, and market impact. Record what the test models and omits.',
                'Order timestamps and types must match a feasible live workflow.',
              ],
            },
            {
              heading: '4. Read behavior behind summary performance',
              paragraphs: [
                'Do not judge a strategy from one return figure. Inspect trade list, drawdowns, exposure, turnover, concentration, holding periods, and dependence on a small number of events.',
                'Trace surprising results back to data and code before making performance claims.',
              ],
            },
            {
              heading: '5. Test robustness without tuning away every failure',
              paragraphs: [
                'Reserve later or unseen data for out-of-sample testing. Vary reasonable dates and parameters to learn whether results depend on one narrow configuration.',
                'Record how many alternatives were tried and why the final configuration was chosen.',
              ],
            },
            {
              heading: '6. Move from historical simulation to operational testing',
              paragraphs: [
                'Paper trading tests more of the live schedule, data connection, order construction, state management, and broker integration without real-money orders.',
                'Paper fills still remain simulations. Compare paper behavior with backtest expectations before any live decision.',
              ],
            },
            {
              heading: 'Limitations and risk',
              paragraphs: [
                'Backtests can be wrong because of bad data, look-ahead bias, overfitting, omitted costs, unrealistic fills, or defects. Even a careful historical test cannot predict future conditions.',
              ],
            },
          ],
          faq: [
            {
              question: 'How much historical data should an AI trading strategy use?',
              answer: 'No single period fits every strategy. Use enough relevant history to observe different conditions while respecting data availability and holding period. Reserve unseen data for evaluation.',
            },
            {
              question: 'Does a strong backtest mean a strategy is ready for live trading?',
              answer: 'No. Backtests do not validate live credentials, broker permissions, network behavior, market impact, or future performance.',
            },
            {
              question: 'Can AI optimize strategy parameters automatically?',
              answer: 'AI can explore parameters, but repeated tuning against one sample increases overfitting risk. Track tests and judge stability rather than the highest result.',
            },
          ],
          relatedLinks: [
            { label: 'Understand backtesting metrics', url: '/guides/backtesting-metrics-explained' },
            { label: 'Avoid strategy overfitting', url: '/guides/avoid-overfitting-trading-strategies' },
            { label: 'Use walk-forward testing', url: '/guides/walk-forward-testing-trading-strategies' },
            { label: 'Prevent look-ahead bias and data leakage', url: '/guides/look-ahead-bias-and-data-leakage' },
            { label: 'Model slippage, fees, and market impact', url: '/guides/modeling-slippage-fees-and-market-impact' },
            { label: 'Build an AI trading bot', url: '/guides/how-to-build-an-ai-trading-bot' },
            { label: 'Compare paper and live trading', url: '/guides/paper-trading-vs-live-trading' },
            { label: 'Review complex backtest case studies', url: '/case-studies' },
            { label: 'Explore supported brokers', url: '/brokers' },
          ],
          sources: [
            { label: 'BotSpot Terms of Service', url: '/terms' },
            { label: 'BotSpot case studies', url: '/case-studies' },
            { label: 'Lumibot backtesting documentation', url: 'https://lumibot.lumiwealth.com/backtesting.html' },
            { label: 'Lumibot lifecycle methods', url: 'https://lumibot.lumiwealth.com/lifecycle_methods.html' },
            { label: 'Alpaca paper trading documentation', url: 'https://docs.alpaca.markets/us/v1.4.2/docs/paper-trading' },
          ],
          verifiedAt: SOURCE_VERIFIED_DATE,
        },
      },
      {
        path: '/guides/paper-trading-vs-live-trading',
        title: 'Paper Trading vs Live Trading | BotSpot Guide',
        description: 'Compare paper and live algorithmic trading, learn what simulations can validate, and use a practical checklist before putting capital at risk.',
        pageType: 'article',
        lastmod: SOURCE_VERIFIED_DATE,
        content: {
          eyebrow: 'Trading deployment guide',
          heading: 'Paper trading vs live trading: know what each stage can prove',
          summary: 'Use backtests for historical logic, paper trading for operational rehearsal, and live trading only after a separate review of broker permissions, execution risk, monitoring, and capital exposure.',
          primaryAction: { label: 'View supported broker modes', url: '/brokers' },
          secondaryAction: { label: 'Read the backtesting guide', url: '/guides/backtesting-ai-trading-strategies' },
          proofPoints: agentProofPoints,
          sections: [
            {
              heading: '1. Separate three different tests',
              paragraphs: [
                'Backtesting, paper trading, and live trading answer different questions. Treating them as interchangeable hides evidence gaps.',
              ],
              bullets: [
                'Backtesting asks how coded rules behaved against historical data under a simulation model.',
                'Paper trading asks whether the strategy operates against current data and a simulated account workflow.',
                'Live trading exposes real capital to broker, exchange, liquidity, latency, fee, and market-impact conditions.',
              ],
            },
            {
              heading: '2. Use paper trading to test operations',
              paragraphs: [
                'Paper trading can reveal scheduling mistakes, stale state, malformed orders, missing data, authentication problems, and monitoring gaps that historical tests miss.',
                'Observe trade and no-trade periods, reconnects, restarts, rejected actions, and agreement between strategy state and broker state.',
              ],
            },
            {
              heading: '3. Know what paper fills cannot reproduce',
              paragraphs: [
                'Paper orders are simulated rather than routed to a live market. Systems may omit or approximate market impact, information leakage, latency slippage, queue position, price improvement, fees, dividends, or available liquidity.',
                'Paper results must remain labeled as simulated, never live performance.',
              ],
            },
            {
              heading: '4. Verify connection and account requirements',
              paragraphs: [
                'BotSpot broker modes vary. Some connections support paper and live workflows; others are live-only. Authentication, assets, permissions, market data, fees, order eligibility, and regions also vary.',
                'Read the current BotSpot broker page and official broker documentation before connecting.',
              ],
              bullets: [
                'Confirm broker, asset, and desired mode are listed.',
                'Verify account approval for intended products and order types.',
                'Check current market-data access and trading-session rules.',
                'Disable withdrawal permissions on API keys where applicable.',
              ],
            },
            {
              heading: '5. Make live deployment a separate decision',
              paragraphs: [
                'Passing a paper test does not authorize live use. Review code, historical assumptions, paper observations, broker configuration, exposure constraints, and monitoring responsibility first.',
                'Define acceptable exposure, order constraints, escalation steps, and a stop process before deployment.',
              ],
            },
            {
              heading: '6. Compare live behavior with paper expectations',
              paragraphs: [
                'After live deployment, compare real order timing, fills, fees, rejections, and position state with backtest and paper assumptions. Investigate differences instead of dismissing them as noise.',
                'Return to paper validation after material changes to logic, broker integration, data, credentials, scheduling, or infrastructure.',
              ],
            },
            {
              heading: 'Limitations and risk',
              paragraphs: [
                'Paper trading is useful for operational testing, but it does not predict live results. Live behavior can differ because of latency, slippage, liquidity, fees, partial fills, market impact, outages, and changing conditions.',
              ],
            },
          ],
          faq: [
            {
              question: 'Does successful paper trading mean a bot is safe to run live?',
              answer: 'No. Paper trading validates parts of the workflow, but fills and account behavior remain simulated. It cannot prove profitability or live execution quality.',
            },
            {
              question: 'How long should a strategy paper trade before going live?',
              answer: 'No fixed duration proves readiness. Testing should cover normal schedule, trade and no-trade conditions, restarts, monitoring, and likely failure paths.',
            },
            {
              question: 'Do paper and live trading use the same broker credentials?',
              answer: 'That depends on the broker. Some use separate keys, accounts, or endpoints. Check the current broker page and official documentation.',
            },
          ],
          relatedLinks: [
            { label: 'Explore supported broker modes', url: '/brokers' },
            { label: 'Backtest AI trading strategies', url: '/guides/backtesting-ai-trading-strategies' },
            { label: 'Build an AI trading bot', url: '/guides/how-to-build-an-ai-trading-bot' },
            { label: 'Use BotSpot with Alpaca', url: '/brokers/alpaca' },
          ],
          sources: [
            { label: 'BotSpot broker connection manifest', url: '/.well-known/botspot-manifest.json' },
            { label: 'BotSpot Terms of Service', url: '/terms' },
            { label: 'Alpaca paper trading documentation', url: 'https://docs.alpaca.markets/us/v1.4.2/docs/paper-trading' },
            { label: 'Alpaca Trading API documentation', url: 'https://docs.alpaca.markets/us/docs/trading-api' },
            { label: 'Lumibot backtesting documentation', url: 'https://lumibot.lumiwealth.com/backtesting.html' },
          ],
          verifiedAt: SOURCE_VERIFIED_DATE,
        },
      },
      ...additionalGuidePages,
    ],
  };
}

module.exports = {
  SEO_EXPANSION_VERIFIED_DATE: SOURCE_VERIFIED_DATE,
  buildSeoExpansionContent,
};
